import { Box, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  COST_DASHBOARD_VARIANT,
  COST_SUMMARY_KINDS,
  COST_SUMMARY_TYPES,
} from 'pages/admin/CostDashboard/constants';
import { AllBusinessCostSummaryFilter } from 'pages/admin/CostDashboard/hooks/useGetAllBusinessSummaries';
import useGetBusinessCostSummary from 'pages/admin/CostDashboard/hooks/useGetBusinessCostSummary';
import useGetPeriodicCostRecords, {
  IUseGetPeriodicCostRecords,
} from 'pages/admin/CostDashboard/hooks/useGetPeriodicCostRecords';
import { useEffect, useMemo, useState } from 'react';
import IconInfo from 'shared/components/icons/IconInfo';
import { Section } from 'shared/components/Section';
import { AreaType } from 'shared/types/protoc-gen/bffbillsystem';

import ResourceRankingTable, {
  ResourceRankingFilter,
} from './components/metrics/ResourceRankingTable';
import { IUseGetCostSummary } from './hooks/useGetCostSummary';
import {
  BusinessResourceTable,
  CostAnalysisFilter,
  CostBarChart,
  CostDetailsFilter,
  CostPieChart,
  CostSummaryFilter,
  OverviewMetricSection,
  PeriodicCostMetric,
} from './components';
import {
  useGetAllBusinessSummaries,
  useGetAllCostSummaries,
  useGetRollupNamespaces,
} from './hooks';

const RollupCost: React.FC = () => {
  const { data: namespaceList } = useGetRollupNamespaces({ type: 'rollup' });

  const [costSummaryFilterProps, setCostSummaryFilterProps] = useState<IUseGetCostSummary>({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    kind: 'ALL',
    type: 'ALL',
    area: 'rollup',
  });

  const [businessCostAnalysisFilter, setBusinessCostAnalysisFilter] =
    useState<AllBusinessCostSummaryFilter>({
      startTime: dayjs().startOf('month').format('YYYY-MM-DD'),
      endTime: dayjs().endOf('month').format('YYYY-MM-DD'),
      namespace: '',
    });

  const [resourceRankingFilter, setResourceRankingFilter] = useState<ResourceRankingFilter>({
    business: 'ALL',
    kind: 'ALL',
    type: 'ALL',
    subResource: 'ALL',
  });

  const [periodicCostRecords, setPeriodicCostRecords] = useState<IUseGetPeriodicCostRecords>({
    startTime: dayjs().startOf('month').format('YYYY-MM-DD'),
    endTime: dayjs().endOf('month').format('YYYY-MM-DD'),
    namespace: '',
    kind: COST_SUMMARY_KINDS.SERVICE,
    type: COST_SUMMARY_TYPES.COMPUTE,
    subresource: 'ALL',
    billingUnitId: undefined,
    resourceName: 'ALL',
  });

  const [barChartPage, setBarChartPage] = useState(1);
  const barChartItemsPerPage = 10;

  useEffect(() => {
    if (namespaceList?.namespace?.length) {
      setBusinessCostAnalysisFilter(prev => ({
        ...prev,
        namespace: namespaceList.namespace[0],
      }));

      setPeriodicCostRecords(prev => ({
        ...prev,
        namespace: namespaceList.namespace[0],
      }));
    }
  }, [namespaceList]);

  const { data: costSummaries, isLoading: isLoadingCostSummaries } = useGetAllCostSummaries({
    ...costSummaryFilterProps,
    namespaceType: 'rollup',
  });

  const { data: periodicCostData, isLoading: isLoadingPeriodicCostRecords } =
    useGetPeriodicCostRecords(periodicCostRecords);

  const { data: businessCostSummaries, isLoading: isLoadingBusinessCostSummaries } =
    useGetAllBusinessSummaries({
      startTime: businessCostAnalysisFilter.startTime,
      endTime: businessCostAnalysisFilter.endTime,
      namespace: businessCostAnalysisFilter.namespace,
      variant: COST_DASHBOARD_VARIANT.ROLLUP,
      area: AreaType.AREA_TYPE_ROLLUP,
    });

  const { data: businessCostSummary } = useGetBusinessCostSummary({
    startTime: businessCostAnalysisFilter.startTime,
    endTime: businessCostAnalysisFilter.endTime,
    namespace: businessCostAnalysisFilter.namespace,
    business: resourceRankingFilter.business,
    kind: resourceRankingFilter.kind,
    type: resourceRankingFilter.type,
    subResource: resourceRankingFilter.subResource,
    area: AreaType.AREA_TYPE_ROLLUP,
    variant: COST_DASHBOARD_VARIANT.ROLLUP,
  });

  const businessCostPieChartData = useMemo(() => {
    return businessCostSummaries
      .filter(entry => (entry?.data?.items?.length ?? 0) > 0)
      .map(entry => ({
        name: entry.businessKey,
        value: entry?.data?.items.reduce((sum, item) => sum + (item.costCounter ?? 0), 0),
      }));
  }, [businessCostSummaries]);

  const namespaceCostPieChartData = useMemo(() => {
    return costSummaries
      .filter(entry => (entry?.data?.items?.length ?? 0) > 0)
      .map(entry => ({
        name: entry.namespace,
        value: entry?.data?.items.reduce((sum, item) => sum + (item.totalCost ?? 0), 0),
      }));
  }, [costSummaries]);

  const namespaceCostBarChartData = useMemo(() => {
    return namespaceCostPieChartData
      .map(item => ({
        name: item.name,
        value: item.value ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [namespaceCostPieChartData]);

  const paginatedNamespaceCostBarChartData = useMemo(() => {
    const sorted = namespaceCostPieChartData
      .map(item => ({
        name: item.name,
        value: item.value ?? 0,
      }))
      .sort((a, b) => b.value - a.value);

    const start = (barChartPage - 1) * barChartItemsPerPage;
    const end = start + barChartItemsPerPage;

    return sorted.slice(start, end);
  }, [namespaceCostPieChartData, barChartPage]);

  return (
    <>
      <OverviewMetricSection variant={COST_DASHBOARD_VARIANT.ROLLUP} />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostSummaryFilter
              {...costSummaryFilterProps}
              setCostFilterProps={setCostSummaryFilterProps}
            />

            <CostPieChart
              data={namespaceCostPieChartData}
              dataKey="value"
              description="Proportional cost allocation across different rollup namespaces within the specified time range."
              height={320}
              isLoading={isLoadingCostSummaries}
              nameKey="name"
              title="Namespace Cost Distribution"
            />

            <CostBarChart
              data={paginatedNamespaceCostBarChartData}
              description="Rollup namespaces ranked by their total costs within the specified time range."
              hasPagination
              isLoading={isLoadingCostSummaries}
              page={barChartPage}
              pageSize={barChartItemsPerPage}
              setPage={setBarChartPage}
              title="Namespace Cost Ranking"
              totalCount={namespaceCostBarChartData.length}
              unit="currency"
            />
          </Stack>
        }
        title="Cost Summary"
      />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostAnalysisFilter
              {...businessCostAnalysisFilter}
              namespaceType="rollup"
              setBusinessCostAnalysisFilter={setBusinessCostAnalysisFilter}
              variant={COST_DASHBOARD_VARIANT.ROLLUP}
            />

            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <CostPieChart
                  data={businessCostPieChartData}
                  dataKey="value"
                  description="Cost allocation across different business categories for a specified rollup within the selected time range."
                  isLoading={isLoadingBusinessCostSummaries}
                  nameKey="name"
                  showTotal
                  title="Business Cost Distribution"
                />

                <BusinessResourceTable
                  businessCostSummaries={businessCostSummaries}
                  variant={COST_DASHBOARD_VARIANT.ROLLUP}
                />
              </Stack>

              <Stack
                spacing={2}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  boxShadow: 1,
                  p: 3,
                  width: '100%',
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="h6">Resource Aggregation</Typography>

                  <Typography variant="subtitle1">
                    Cost aggregation for resources within specific business categories based on
                    kubernetes labels.
                  </Typography>
                </Stack>

                <ResourceRankingTable
                  {...resourceRankingFilter}
                  businessCostSummary={businessCostSummary}
                  setResourceRankingFilter={setResourceRankingFilter}
                  variant={COST_DASHBOARD_VARIANT.ROLLUP}
                />
              </Stack>
            </Stack>
          </Stack>
        }
        renderTitleSuffix={
          <Tooltip
            placement="top"
            title="Cost breakdown categorizing rollup resources by business functions based on kubernetes labels."
          >
            <Box alignItems="center" display="flex" justifyContent="center" sx={{ pb: 0.5 }}>
              <IconInfo sx={{ height: '18px', width: '18px' }} />
            </Box>
          </Tooltip>
        }
        title="Business Cost Analysis"
      />

      <Section
        renderContent={
          <Stack spacing={5} sx={{ p: 3 }}>
            <CostDetailsFilter
              {...periodicCostRecords}
              namespaceType="rollup"
              setCostDetailsFilter={setPeriodicCostRecords}
            />

            <PeriodicCostMetric
              isLoading={isLoadingPeriodicCostRecords}
              periodicCostData={periodicCostData}
            />
          </Stack>
        }
        renderTitleSuffix={
          <Tooltip
            placement="top"
            title="The most granular view of resource costs with filtering capabilities and time-series visualization for individual billing units."
          >
            <Box alignItems="center" display="flex" justifyContent="center" sx={{ pb: 0.5 }}>
              <IconInfo sx={{ height: '18px', width: '18px' }} />
            </Box>
          </Tooltip>
        }
        title="Cost Details"
      />
    </>
  );
};

export default RollupCost;
